import React, { useEffect, useState } from 'react';
import { webNavigate } from '../../services/navigate.service';
import { getLocations } from '../../services/inventoryCorrections.service';
import { getDetailReasons } from '../../services/reason.service';
import { deleteTransfer, getTransfersOverview, TransfersOverview } from '../../services/transfer.service';
import { showDeleteToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import { useResponsive } from '../../hook/useResponsive';
import { useDebounceEffect } from '../../hook/useDebounceEffect';
import {
  Button,
  ConfirmDialog,
  DocListRow,
  DocumentTable,
  EmptyState,
  ErrorBanner,
  FilterDef,
  FiltersSheet,
  ListState,
  MenuItem,
  PageFrame,
  Pager,
  PageTitle,
  PhoneDocumentList,
  SharePanel,
  StatusStrip,
  ToolbarCard,
  TRANSFER_STATUS,
  TRANSFER_STATUS_GROUPS,
  dayMonth,
  money,
  periodTextOf,
  rangeOf,
  readListState,
  transferStatusOf,
  useLanguage,
  useOverviewData,
  writeListState,
} from '../../components/purchasing';
import NewTransferDialog from './NewTransferDialog';

const PAGE_SIZE = 20;
const STATUS_KEYS = TRANSFER_STATUS_GROUPS.map((group) => group.key);

/** Transfers between the company's sites: the header site's by default, in and out. */
const Transfer = () => {
  const preLanguage = localStorage.getItem('language');
  const userId = localStorage.getItem('userId') || '';
  const companyId = localStorage.getItem('companyId') || '';
  const headerSite = localStorage.getItem('locationId') || '';
  const { isMobile: phone } = useResponsive();
  const { language, t } = useLanguage();

  const [state, setState] = useState<ListState>(() => readListState(STATUS_KEYS, ['direction']));
  const [term, setTerm] = useState(state.search.trim());
  const [sites, setSites] = useState<{ id: string; name: string }[]>([]);
  const [reasons, setReasons] = useState<{ id: string; name: string }[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // /transfer/new lands here with the dialog open.
  const [creating, setCreating] = useState(() => /(^|[?&])new=1(&|$)/.test(location.search));
  const [deleting, setDeleting] = useState<DocListRow | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const update = (patch: Partial<ListState>) => setState((prev) => ({ ...prev, page: 1, ...patch }));
  const setFilter = (key: string, value: string) => setState((prev) => ({ ...prev, page: 1, filters: { ...prev.filters, [key]: value } }));

  useEffect(() => writeListState(state), [state]);
  useDebounceEffect(() => setTerm(state.search.trim()), [state.search], 300);

  useEffect(() => {
    const load = () => {
      getLocations(userId, companyId, 0, 0, '')
        .then((response: any) => setSites((response?.data?.data ?? []).map((row: any) => ({ id: String(row.id), name: String(row.name ?? '') }))))
        .catch(() => undefined);
      getDetailReasons(companyId, '3')
        .then((response: any) => setReasons((response?.data?.data ?? []).map((row: any) => ({ id: String(row.id), name: String(row.reasonName ?? '') }))))
        .catch(() => undefined);
    };
    load();
    document.addEventListener('newToken', load);
    return () => document.removeEventListener('newToken', load);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // In or out only means something from one site.
  const direction = state.site && ['in', 'out'].includes(state.filters.direction) ? (state.filters.direction as 'in' | 'out') : undefined;
  const { data, loading, failed, reload } = useOverviewData<TransfersOverview>(
    () =>
      getTransfersOverview({
        ...rangeOf(state),
        status: state.status === 'all' ? undefined : state.status,
        direction,
        search: term || undefined,
        locationId: state.site || undefined,
        page: state.page,
        limit: PAGE_SIZE,
      }),
    [state.period, state.from, state.to, state.status, state.site, direction, state.page, term],
    language,
  );

  const siteName = sites.find((site) => site.id === state.site)?.name;
  const periodText = periodTextOf(state, t);
  const totals = data?.totals ?? { transfers: 0, value: 0 };
  const transfersText = (count: number) => (count === 1 ? t('1 transfer') : t('{count} transfers', { count }));
  const linesText = (count: number) => (count === 1 ? t('1 line') : t('{count} lines', { count }));
  // The count matches All in the strip; the value leaves cancelled transfers out.
  const meta = [
    phone ? null : state.site ? siteName : t('All sites'),
    periodText,
    data ? transfersText(Number(data.statusCounts?.all ?? 0)) : null,
    data ? t('{amount} at cost', { amount: money(totals.value) }) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const counts = data?.statusCounts ?? {};
  const strip = TRANSFER_STATUS_GROUPS.filter((group) => group.key !== 'cancelled' || (counts.cancelled ?? 0) > 0 || state.status === 'cancelled').map((group) => ({
    key: group.key,
    label: t(group.label),
    family: group.family,
    count: Number(counts[group.key] ?? 0),
  }));

  const statusIds = new Map((data?.rows ?? []).map((row) => [row.id, row.statusId]));
  // A transfer opens from one of its own sites: the header site when it is one, else where it left.
  const viewSites = new Map((data?.rows ?? []).map((row) => [row.id, [row.from?.id, row.to?.id].includes(headerSite) ? headerSite : row.from?.id]));
  const rows: DocListRow[] = (data?.rows ?? []).map((row) => {
    const from = row.from?.name ?? '—';
    const to = row.to?.name ?? '—';
    const sent = row.statusId !== TRANSFER_STATUS.Draft && row.sentAt ? dayMonth(row.sentAt) : null;
    const sender = sent ? row.sentBy?.name ?? null : null;
    return {
      id: row.id,
      number: `#${row.internalNumber}`,
      subtitle: row.reason?.name ?? null,
      cells: {
        route: { main: from, sub: `→ ${to}` },
        sent: { main: sent ?? '—', sub: sender },
        lines: { main: row.lines },
        value: { main: money(row.value), strong: true },
      },
      status: transferStatusOf(row.statusId),
      phoneText: [`${from} → ${to}`, row.reason?.name].filter(Boolean).join(' · '),
      phoneMeta: [sent ? t('Sent {date}', { date: sent }) : null, linesText(row.lines)].filter(Boolean).join(' · '),
      phoneAmount: money(row.value),
    };
  });

  const hrefOf = (row: DocListRow) => {
    const site = viewSites.get(row.id);
    return `/${preLanguage}/transfer/${row.id}${site && site !== headerSite ? `?site=${site}` : ''}`;
  };
  const open = (row: DocListRow) => webNavigate(hrefOf(row));
  const menuFor = (row: DocListRow): MenuItem[] => [
    { key: 'open', label: t('Open transfer'), icon: 'arrowRight', onSelect: () => open(row) },
    ...(statusIds.get(row.id) === TRANSFER_STATUS.Draft ? [{ key: 'delete', label: t('Delete draft'), icon: 'trash', danger: true, onSelect: () => setDeleting(row) }] : []),
  ];

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await deleteTransfer(deleting.id, viewSites.get(deleting.id) || headerSite);
      showDeleteToast(t('Transfer deleted'));
      setDeleting(null);
      reload();
    } catch (error: any) {
      showApiError(error, { language });
    } finally {
      setDeleteBusy(false);
    }
  };

  const filters: FilterDef[] = [
    { key: 'site', label: 'Site', icon: 'pin', allLabel: 'All sites', value: state.site, options: sites, onChange: (value) => update({ site: value }) },
    ...(state.site
      ? [
          {
            key: 'direction',
            label: 'Direction',
            icon: 'arrowRight' as const,
            allLabel: 'In and out',
            value: direction ?? '',
            options: [
              { id: 'in', name: t('Coming in') },
              { id: 'out', name: t('Going out') },
            ],
            onChange: (value: string) => setFilter('direction', value),
          },
        ]
      : []),
  ];

  const newTransfer = () => setCreating(true);
  const statusLabel = TRANSFER_STATUS_GROUPS.find((group) => group.key === state.status)?.label;
  const empty = term ? (
    <EmptyState icon="search" phone={phone} title={t('No transfers match "{search}"', { search: term })} desc={t('Check the number, or search by site, reason or product.')} />
  ) : state.status !== 'all' && statusLabel ? (
    <EmptyState phone={phone} title={t('No transfers with this status')} desc={t('{status} transfers in this period show here.', { status: t(statusLabel) })} />
  ) : (
    <EmptyState
      phone={phone}
      title={t('No transfers in this period')}
      desc={t('Move stock between your sites with a transfer.')}
      action={
        <Button variant="primary" phone={phone} icon="plus" onClick={newTransfer}>
          {t('New transfer')}
        </Button>
      }
    />
  );

  const pager = data ? <Pager phone={phone} t={t} page={state.page} limit={PAGE_SIZE} total={data.count} onPage={(page) => setState((prev) => ({ ...prev, page }))} /> : null;
  const list = phone ? (
    <PhoneDocumentList t={t} label={t('Transfers')} rows={rows} loading={loading} hrefOf={hrefOf} onOpen={open} empty={empty} footer={pager} />
  ) : (
    <DocumentTable
      t={t}
      label={t('Transfers')}
      numberLabel={t('Transfer')}
      columns={[
        { key: 'route', label: t('Route') },
        { key: 'sent', label: t('Sent'), align: 'right', compact: true },
        { key: 'lines', label: t('Lines'), align: 'right', compact: true },
        { key: 'value', label: t('Value'), align: 'right', compact: true },
      ]}
      rows={rows}
      loading={loading}
      hrefOf={hrefOf}
      onOpen={open}
      menuFor={menuFor}
      menuLabel={(row) => t('Actions for transfer {number}', { number: row.number })}
      triggerLabel={(row) => t('More actions for transfer {number}', { number: row.number })}
      empty={empty}
      footer={pager}
    />
  );

  const panel = (
    <SharePanel
      label={t('By route')}
      title={t('By route')}
      subtitle={data ? t('{period} · {transfers} · value at cost', { period: periodText, transfers: transfersText(totals.transfers) }) : undefined}
      empty={t('No transfers to sum up in this period.')}
      rows={(data?.byRoute ?? []).map((row) => {
        const share = totals.value > 0 ? Math.round((Number(row.value) / totals.value) * 100) : 0;
        return {
          key: `${row.fromId}-${row.toId}`,
          name: `${row.fromName ?? '—'} → ${row.toName ?? '—'}`,
          value: money(row.value),
          detail: `${transfersText(row.transfers)} · ${share}%`,
          share,
        };
      })}
    />
  );

  return (
    <PageFrame phone={phone}>
      <PageTitle phone={phone} t={t} title={t('Transfers')} meta={meta} primary={{ key: 'new', label: t('New transfer'), icon: 'plus', onSelect: newTransfer }} />
      <ToolbarCard
        phone={phone}
        t={t}
        period={state.period}
        onPeriod={(period) => update({ period })}
        customFrom={state.from}
        customTo={state.to}
        onCustom={(from, to) => update({ from, to })}
        search={state.search}
        onSearch={(search) => setState((prev) => ({ ...prev, search, page: 1 }))}
        searchLabel={t('Search transfers')}
        searchPlaceholder={phone ? t('Search transfers') : t('Number, site or product')}
        filters={filters}
        onOpenFilters={() => setFiltersOpen(true)}
      />
      <StatusStrip phone={phone} label={t('Transfer status')} items={strip} active={state.status} onPick={(status) => update({ status })} />
      {failed && <ErrorBanner message={t('The transfers could not be loaded.')} retryLabel={t('Try again')} onRetry={reload} />}
      {phone ? (
        list
      ) : (
        <div className="pu-split-wide" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(300px, 320px)', gap: '24px', alignItems: 'start' }}>
          <div style={{ minWidth: 0 }}>{list}</div>
          <div style={{ minWidth: 0 }}>{panel}</div>
        </div>
      )}
      {phone && (
        <FiltersSheet
          open={filtersOpen}
          t={t}
          filters={filters}
          showLabel={data?.count === 1 ? t('Show 1 transfer') : t('Show {count} transfers', { count: data?.count ?? 0 })}
          onClear={() => setState((prev) => ({ ...prev, page: 1, site: '', filters: { ...prev.filters, direction: '' } }))}
          onClose={() => setFiltersOpen(false)}
        />
      )}
      <NewTransferDialog
        open={creating}
        phone={phone}
        t={t}
        language={language}
        sites={sites}
        reasons={reasons}
        defaultFromId={state.site || headerSite}
        onClose={() => setCreating(false)}
        onCreated={(id) => {
          setCreating(false);
          webNavigate(`/${preLanguage}/transfer/${id}`);
        }}
      />
      <ConfirmDialog
        open={!!deleting}
        phone={phone}
        danger
        busy={deleteBusy}
        title={t('Delete draft transfer {number}?', { number: deleting?.number ?? '' })}
        body={t('The draft and its products are removed. This cannot be undone.')}
        confirmLabel={t('Delete draft')}
        cancelLabel={t('Keep draft')}
        closeLabel={t('Close')}
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </PageFrame>
  );
};

export default Transfer;
